import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/dist/query';
import apiSlice from '..';
import { User } from '../../../types/user';

export type UserLocationEventActionType = 'signup' | 'login' | 'bid' | 'offer' | 'counter_offer' | 'award';

export interface UserLocationEventRow {
  id: number;
  created_at: string;
  user_id: number;
  vehicle_id: number | null;
  action_type: UserLocationEventActionType;
  ip: string;
  city: string | null;
  region: string | null;
  country: string | null;
}

export interface UserLocationEvents {
  rows: UserLocationEventRow[];
  count: number;
}

type UserLocationEventsArgs = {
  id: number | string;
  limit: number;
  page: number;
};

export type ImpersonationActivityType =
  | 'login'
  | 'logout'
  | 'place_bid'
  | 'place_offer'
  | 'place_counter'
  | 'award'
  | 'accept_bid'
  | 'accept_offer';

export interface ImpersonationActivityAdmin {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  job_title: string | null;
  dealership_name: string | null;
}

export interface ImpersonationActivityRow {
  id: number;
  created_at: string;
  admin_id: number;
  user_id: number;
  vehicle_id: number | null;
  activity: ImpersonationActivityType;
  admin: ImpersonationActivityAdmin | null;
}

export interface ImpersonationActivity {
  rows: ImpersonationActivityRow[];
  count: number;
}

type ImpersonationActivityArgs = {
  id: number | string;
  perPage: number;
  page: number;
};

export const adminUsersApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getUserLocationEvents: builder.query<UserLocationEvents, UserLocationEventsArgs>({
      query: ({ id, limit, page }) => ({
        url: `admin/users/${id}/location-events`,
        method: 'GET',
        params: { limit, page }
      }),
      transformResponse: (r: any) => ({
        rows: r.data.rows,
        count: Number(r.data.count)
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { page, ...restArgs } = queryArgs;
        return defaultSerializeQueryArgs({ queryArgs: restArgs, endpointDefinition, endpointName });
      },
      merge: (currentCache, newItems, { arg }) => {
        currentCache.count = newItems.count;
        if (arg.page === 1) {
          currentCache.rows = newItems.rows;
        } else {
          currentCache.rows.push(...newItems.rows);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      }
    }),
    getImpersonationActivity: builder.query<ImpersonationActivity, ImpersonationActivityArgs>({
      query: ({ id, perPage, page }) => ({
        url: `admin/customers/${id}/impersonation-activity`,
        method: 'GET',
        params: { perPage, page }
      }),
      transformResponse: (r: any) => ({
        rows: r.data.rows,
        count: Number(r.data.count)
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { page, ...restArgs } = queryArgs;
        return defaultSerializeQueryArgs({ queryArgs: restArgs, endpointDefinition, endpointName });
      },
      merge: (currentCache, newItems, { arg }) => {
        currentCache.count = newItems.count;
        if (arg.page === 1) {
          currentCache.rows = newItems.rows;
        } else {
          currentCache.rows.push(...newItems.rows);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      }
    }),
    updateLocationAlert: builder.mutation<void, { id: number | string; disable: boolean }>({
      query: ({ id, disable }) => ({
        url: `admin/customers/${id}/location-alert`,
        method: 'PATCH',
        body: { disable }
      })
    }),
    createFakeUser: builder.mutation<{ data: User }, { dealership_name: string; company_name: string }>({
      query: payload => ({
        url: `/admin/users/fake`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['FakeUsers']
    }),
    updateFakeUser: builder.mutation<void, { dealership_name: string; company_name: string; id: number }>({
      query: ({ id, ...payload }) => ({
        url: `/admin/users/fake/${id}`,
        method: 'PATCH',
        body: payload
      }),
      invalidatesTags: ['FakeUsers', 'AuctionVehicles']
    }),
    deleteFakeUser: builder.mutation<void, number>({
      query: id => ({
        url: `/admin/users/fake/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['FakeUsers', 'AuctionVehicles']
    }),
    getFakeUsers: builder.query<{ acv: User[]; other: User[]; open_lane: User[] }, void>({
      query: () => ({
        url: `/admin/users/fake`,
        method: 'GET'
      }),
      providesTags: ['FakeUsers'],
      transformResponse: (r: any) => r.data
    }),
    getFakeUserList: builder.query<User[], void>({
      query: () => ({
        url: 'admin/users/fake/list',
        method: 'GET'
      }),
      providesTags: ['FakeUsers'],
      transformResponse: (r: any) => r.data
    })
  }),
  overrideExisting: true
});

export const {
  useGetFakeUsersQuery,
  useCreateFakeUserMutation,
  useGetFakeUserListQuery,
  useUpdateFakeUserMutation,
  useDeleteFakeUserMutation,
  useGetUserLocationEventsQuery,
  useGetImpersonationActivityQuery,
  useUpdateLocationAlertMutation
} = adminUsersApi;
export default adminUsersApi;
